// frontend/src/components/PillInput.js
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";

import { X } from "phosphor-react";
import { api } from "../api";

const normalizeValue = (value = "") =>
  String(value)
    .trim()
    .replace(/^#+/, "")
    .replace(/\s+/g, " ");

const parseValues = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeValue).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map(normalizeValue)
    .filter(Boolean);
};

export default function PillInput({
  value,
  onChange,
  maxCount = 5,

  // searchable=true = currently exploring
  // searchable=false = places that feel like home
  searchable = false,

  placeholder = "Add one",
  anotherPlaceholder = "Add another",
}) {
  const values = useMemo(() => parseValues(value), [value]);

  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const pillRowRef = useRef(null);
  const [inputStartsRow, setInputStartsRow] = useState(false);

  const [loading, setLoading] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const emitValues = useCallback(
    (nextValues) => {
      const unique = [];
      const seen = new Set();

      nextValues.forEach((rawValue) => {
        const item = normalizeValue(rawValue);
        const key = item.toLowerCase();

        if (!item || seen.has(key)) return;

        seen.add(key);
        unique.push(item);
      });

      onChange(unique.slice(0, maxCount));
    },
    [maxCount, onChange]
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!searchable) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const query = normalizeValue(draft);

    if (!query) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        const response = await api.get(
          "/activities/tags/search",
          {
            params: {
              q: query,
              limit: 8,
            },
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : undefined,
            signal: controller.signal,
          }
        );

        const existingKeys = new Set(
          values.map((item) => item.toLowerCase())
        );

        const nextSuggestions = Array.isArray(
          response.data?.tags
        )
          ? response.data.tags
              .map(normalizeValue)
              .filter(Boolean)
              .filter(
                (item) =>
                  !existingKeys.has(item.toLowerCase())
              )
          : [];

        setSuggestions(nextSuggestions);
        setActiveIndex(0);
        setOpen(true);
      } catch (error) {
        if (
          error?.name !== "CanceledError" &&
          error?.code !== "ERR_CANCELED"
        ) {
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 180);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [draft, searchable, values]);

  useEffect(() => {
    const checkInputRow = () => {
      if (
        !pillRowRef.current ||
        !inputRef.current ||
        values.length === 0
      ) {
        setInputStartsRow(false);
        return;
      }

      const pills = Array.from(
        pillRowRef.current.querySelectorAll("[data-pill]")
      );

      const inputRect =
        inputRef.current.getBoundingClientRect();

      const inputCenterY =
        inputRect.top + inputRect.height / 2;

      const pillOnSameRowBeforeInput = pills.some(
        (pill) => {
          const pillRect =
            pill.getBoundingClientRect();

          const pillCenterY =
            pillRect.top + pillRect.height / 2;

          const sameRow =
            Math.abs(pillCenterY - inputCenterY) < 5;

          const beforeInput =
            pillRect.left < inputRect.left;

          return sameRow && beforeInput;
        }
      );

      setInputStartsRow(!pillOnSameRowBeforeInput);
    };

    checkInputRow();

    const observer = new ResizeObserver(checkInputRow);

    if (pillRowRef.current) {
      observer.observe(pillRowRef.current);
    }

    return () => observer.disconnect();
  }, [values]);

  const commitValue = useCallback(
    (rawValue) => {
      if (values.length >= maxCount) return;

      const item = normalizeValue(rawValue);

      if (!item) return;

      const exists = values.some(
        (existing) =>
          existing.toLowerCase() === item.toLowerCase()
      );

      if (!exists) {
        emitValues([...values, item]);
      }

      setDraft("");
      setSuggestions([]);
      setOpen(false);
      setActiveIndex(0);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [emitValues, maxCount, values]
  );

  const removeValue = useCallback(
    (indexToRemove) => {
      emitValues(
        values.filter(
          (_, index) => index !== indexToRemove
        )
      );

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [emitValues, values]
  );

  const handleKeyDown = (event) => {
    if (
      event.key === "ArrowDown" &&
      open &&
      suggestions.length > 0
    ) {
      event.preventDefault();

      setActiveIndex((index) =>
        index >= suggestions.length - 1
          ? 0
          : index + 1
      );

      return;
    }

    if (
      event.key === "ArrowUp" &&
      open &&
      suggestions.length > 0
    ) {
      event.preventDefault();

      setActiveIndex((index) =>
        index <= 0
          ? suggestions.length - 1
          : index - 1
      );

      return;
    }

    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();

      if (open && suggestions[activeIndex]) {
        commitValue(suggestions[activeIndex]);
      } else {
        commitValue(draft);
      }

      return;
    }

    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (
      event.key === "Backspace" &&
      !draft &&
      values.length > 0
    ) {
      removeValue(values.length - 1);
    }
  };

  const normalizedDraft = normalizeValue(draft);

  const exactSuggestionExists = suggestions.some(
    (suggestion) =>
      suggestion.toLowerCase() ===
      normalizedDraft.toLowerCase()
  );

  const atMax = values.length >= maxCount;

  return (
    <div
      ref={wrapperRef}
      className="relative"
    >
      <div
        ref={pillRowRef}
        className={`
          flex min-h-[34px] w-full flex-wrap items-center gap-1
          rounded-2xl border border-gray-200 bg-white
          !min-h-0 !min-w-0
          ${values.length === 0 ? "py-1.5 px-3" : "py-1 px-2"}
        `}
        onClick={() => {
          if (!atMax) {
            inputRef.current?.focus();
          }
        }}
      >
        {values.map((item, index) => (
          <span
            data-pill
            key={`${item}-${index}`}
            className="
              inline-flex shrink-0 items-center gap-1
              rounded-full bg-blue-100 font-medium
              !min-h-0 px-2 py-0.5 text-xs text-blue-700
            "
          >
            {item}

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                removeValue(index);
              }}
              className="
                inline-flex !min-h-0 !min-w-0
                items-center justify-center
                rounded-full hover:text-red-500
              "
              aria-label={`Remove ${item}`}
            >
              <X size={9} weight="bold" />
            </button>
          </span>
        ))}

        {!atMax && (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              if (
                searchable &&
                normalizedDraft
              ) {
                setOpen(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              values.length === 0
                ? placeholder
                : anotherPlaceholder
            }
            className={`
              min-w-[34px] max-w-full flex-1 border-0
              bg-transparent py-0 !min-h-0
              outline-none focus:ring-0
              text-xs
              ${inputStartsRow ? "ml-1" : "ml-0"}
            `}
            autoComplete="off"
          />
        )}
      </div>

      {searchable &&
        open &&
        normalizedDraft &&
        !atMax && (
          <div
            className="
              absolute left-0 right-0 z-40 mt-1
              max-h-52 overflow-y-auto
              rounded-2xl border bg-white
              p-1 shadow-lg 
            "
          >
            {loading &&
            suggestions.length === 0 ? (
              <p className="!min-h-0 !min-w-0 px-3 py-1 text-xs text-gray-500">
                Searching…
              </p>
            ) : (
              <>
                {suggestions.map(
                  (suggestion, index) => (
                    <button
                      key={suggestion}
                      type="button"
                      onMouseDown={(event) =>
                        event.preventDefault()
                      }
                      onClick={() =>
                        commitValue(suggestion)
                      }
                      className={[
                        "block w-full rounded-xl !min-h-0 !min-w-0 px-2 py-1 text-left text-xs",
                        index === activeIndex
                          ? "bg-blue-100 text-blue-700"
                          : "hover:bg-gray-50",
                      ].join(" ")}
                    >
                      {suggestion}
                    </button>
                  )
                )}

                {!exactSuggestionExists && (
                  <button
                    type="button"
                    onMouseDown={(event) =>
                      event.preventDefault()
                    }
                    onClick={() =>
                      commitValue(normalizedDraft)
                    }
                    className="
                      block w-full rounded-xl
                      !min-h-0 !min-w-0 px-2 py-0.5 text-left
                      text-xs text-gray-600
                      hover:bg-gray-50
                    "
                  >
                    Add{" "}
                    <span className="font-medium text-blue-600">
                      {normalizedDraft}
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      
      {!searchable &&
        normalizedDraft &&
        !atMax && (
          <div
            className="
              absolute left-0 right-0 z-40 mt-1
              rounded-2xl border bg-white
              p-1 shadow-lg
            "
          >
            <button
              type="button"
              onMouseDown={(event) =>
                event.preventDefault()
              }
              onClick={() =>
                commitValue(normalizedDraft)
              }
              className="
                block w-full rounded-xl
                !min-h-0 !min-w-0 px-2 py-0.5 text-left
                text-xs text-gray-600
                hover:bg-gray-50
              "
            >
              Add{" "}
              <span className="font-medium text-blue-600">
                {normalizedDraft}
              </span>
            </button>
          </div>
        )}
    </div>
  );
}